import React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import { useRouter } from 'expo-router'
import type { DiceLuckHistory, DiceLuckHistoryGame } from '@warcarp/api-types'
import { DICE_LUCK_PERCENTILE_MIN_ROLLS } from '@warcarp/api-types'
import {
  diceLuckVerdictToneClass,
  formatDiceLuckSwing,
  formatDiceLuckVerdict,
} from '../../lib/diceLuck'

function GameExtreme({
  label,
  game,
  onPress,
}: {
  label: string
  game: DiceLuckHistoryGame
  onPress: () => void
}) {
  const date = new Date(game.completedAt).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  })
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`View ${label.toLowerCase()}`}
      testID={`dice-luck-${label.toLowerCase().replace(' ', '-')}`}
      className="flex-1 min-w-[132px] rounded-lg border border-olive bg-paper-up px-3 py-3 gap-1"
    >
      <Text className="font-stencil text-[10px] uppercase tracking-widest text-ink-soft">{label}</Text>
      <Text className="font-stencil text-xl text-ink">{formatDiceLuckSwing(game.netSwing)}</Text>
      <Text className="font-typed text-xs text-ink-soft">{date} · {game.rollCount} rolls</Text>
      <Text className="font-typed text-xs text-ink-soft">Standard score {game.zScore > 0 ? '+' : ''}{game.zScore.toFixed(2)}</Text>
      <Text className="font-stencil text-[10px] uppercase text-blood mt-1">View game →</Text>
    </TouchableOpacity>
  )
}

export function DiceLuckHistoryPanel({ history }: { history: DiceLuckHistory }) {
  const router = useRouter()

  return (
    <View className="bg-paper border-2 border-olive rounded-xl p-4 gap-3">
      <Text className="font-stencil text-ink uppercase text-sm tracking-widest">
        Dice Luck History
      </Text>
      {history.gamesRecorded === 0 ? (
        <Text className="font-typed text-ink-soft text-xs italic">
          // no completed games with recorded dice luck yet
        </Text>
      ) : (
        <>
          <View className="flex-row items-end justify-between gap-3">
            <View className="flex-1">
              <Text className="font-stencil text-[10px] uppercase tracking-widest text-ink-soft">
                All-time dice swing
              </Text>
              <Text className="font-stencil text-ink text-2xl">{formatDiceLuckSwing(history.netSwing)} armies</Text>
            </View>
            <View className="items-end">
              <Text
                testID="dice-luck-history-verdict"
                className={`font-stencil text-sm uppercase ${diceLuckVerdictToneClass(history.verdict)}`}
              >
                {formatDiceLuckVerdict(history.verdict)}
              </Text>
              <Text className="font-typed text-ink-soft text-xs">{history.rollCount} involved rolls</Text>
            </View>
          </View>

          <View className="flex-row gap-2">
            <View className="flex-1 rounded-md bg-paper-up px-3 py-2 gap-0.5">
              <Text className="font-typed text-ink-soft text-[10px] uppercase tracking-widest">Attacking</Text>
              <Text className="font-stencil text-ink text-lg">{formatDiceLuckSwing(history.attackSwing)}</Text>
            </View>
            <View className="flex-1 rounded-md bg-paper-up px-3 py-2 gap-0.5">
              <Text className="font-typed text-ink-soft text-[10px] uppercase tracking-widest">Defending</Text>
              <Text className="font-stencil text-ink text-lg">{formatDiceLuckSwing(history.defenseSwing)}</Text>
            </View>
          </View>

          {history.percentile !== null ? (
            <Text className="font-typed text-ink text-sm">
              Luckier than {Math.round(history.percentile)}% of equivalent dice sequences.
            </Text>
          ) : (
            <Text className="font-typed text-ink-soft text-xs">
              Early results — the score and label may shift quickly with more rolls. Estimated percentiles appear at {DICE_LUCK_PERCENTILE_MIN_ROLLS} rolls.
            </Text>
          )}

          {history.bestGame && history.worstGame && (
            <View className="flex-row flex-wrap gap-2">
              <GameExtreme
                label="Luckiest game"
                game={history.bestGame}
                onPress={() => router.push(`/(app)/games/${history.bestGame!.gameId}`)}
              />
              <GameExtreme
                label="Unluckiest game"
                game={history.worstGame}
                onPress={() => router.push(`/(app)/games/${history.worstGame!.gameId}`)}
              />
            </View>
          )}
        </>
      )}
    </View>
  )
}
